import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Trophy, NotebookPen, Loader2, Sparkles } from "lucide-react";
import { API } from "./coach/SharedComponents";

/**
 * WinsOfTheWeekPanel — Hormozi-style *celebration* outreach.
 * Surfaces athletes with positive momentum this week so the coach can
 * send a high-five note. Celebration > callouts.
 *
 * Reuses `CoachNoteModal` exported by `AtRiskAthletesPanel` for the note action.
 */
export const WinsOfTheWeekPanel = ({ onLeaveNote }) => {
  const [wins, setWins] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchWins = async () => {
      setLoading(true);
      try {
        const r = await fetch(`${API}/coach/weekly-wins`, { credentials: "include" });
        if (r.ok) {
          const d = await r.json();
          setWins(d.athletes || []);
        }
      } catch (e) {
        console.error("weekly-wins fetch failed", e);
      } finally {
        setLoading(false);
      }
    };
    fetchWins();
  }, []);

  if (loading) {
    return (
      <div className="bg-[#141B2D] border border-[#1E293B] rounded-2xl md:rounded-3xl p-5 md:p-6 h-80 flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-emerald-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="bg-[#141B2D] border border-[#1E293B] rounded-2xl md:rounded-3xl p-5 md:p-6" data-testid="wins-of-the-week-panel">
      <div className="flex items-center justify-between mb-4 md:mb-6">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-emerald-500/15 flex items-center justify-center">
            <Trophy className="w-4 h-4 text-emerald-400" />
          </div>
          <div>
            <h2 className="text-lg md:text-xl font-bold text-white">Wins of the Week</h2>
            <p className="text-xs text-gray-500">Athletes outpacing last week</p>
          </div>
        </div>
        <span className="text-xs font-semibold bg-emerald-500/10 text-emerald-400 px-2.5 py-1 rounded-full">
          {wins.length}
        </span>
      </div>

      {wins.length === 0 ? (
        <div className="py-12 text-center">
          <Sparkles className="w-10 h-10 text-gray-600 mx-auto mb-3" />
          <p className="text-white font-semibold">No standout wins yet this week</p>
          <p className="text-sm text-gray-500 mt-1">
            Athletes with more sessions than last week will show up here.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {wins.map((a) => (
            <motion.div
              key={a.user_id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center gap-3 p-4 bg-[#0B0F1A] border border-[#1E293B] rounded-xl hover:border-emerald-500/30 transition-colors"
              data-testid={`win-athlete-${a.user_id}`}
            >
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-600/30 to-emerald-500/30 flex items-center justify-center flex-shrink-0">
                <span className="text-sm font-bold text-white">
                  {(a.name || a.email || "?").slice(0, 1).toUpperCase()}
                </span>
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-white truncate">{a.name || a.email}</p>
                <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                  <span className="text-emerald-400 font-semibold">+{a.delta}</span>
                  <span>vs last week</span>
                  <span className="text-gray-700">·</span>
                  <span>{a.this_week} this week</span>
                </div>
              </div>
              <button
                onClick={() => onLeaveNote && onLeaveNote(a)}
                className="flex items-center gap-1.5 px-3 py-2 bg-gradient-to-r from-emerald-600 to-emerald-500 hover:shadow-lg hover:shadow-emerald-500/25 text-white text-xs font-semibold rounded-lg transition-all flex-shrink-0"
                data-testid={`high-five-${a.user_id}`}
              >
                <NotebookPen className="w-3.5 h-3.5" />
                High-Five
              </button>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default WinsOfTheWeekPanel;
